import type { Seat } from '@campfire/engine';
import { SEAT_NAMES, SIZE, isKing, jumpedSquares, ownerOf, type CheckersView } from '@campfire/game-checkers';
import { label, yOf } from '@campfire/grid';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { gridBoard } from './grid-board.ts';

const startsWith = (p: readonly number[], prefix: readonly number[]) => prefix.every((v, i) => p[i] === v);
const isCapture = (p: readonly number[]) => Math.abs(yOf(SIZE, p[1] as number) - yOf(SIZE, p[0] as number)) === 2;

function player(ctx: TableCtx, v: CheckersView, s: Seat, pos: string) {
  const turn = v.turn === s;
  return h(
    `div.ck-player${turn ? '.turn' : ''}`,
    { 'data-testid': `ck-player-${pos}`, 'data-seat': s },
    h(`span.ck-swatch.s${s}`),
    h('span.ck-name', {}, name(ctx, s)),
    h('span.muted', {}, SEAT_NAMES[s]),
    h('span.ck-count', { 'data-testid': `ck-captured-${s}`, 'data-count': v.captures[s] }, `${v.captures[s]} captured`),
    h('span.muted', { 'data-testid': `ck-left-${s}` }, `${v.remaining[s]} left`),
  );
}

function statusText(ctx: TableCtx, v: CheckersView, mid: boolean, mustCapture: boolean): string {
  if (v.result) {
    const w = v.result.winners[0];
    return v.result.draw ? `Draw: ${v.result.reason ?? ''}` : `${w === ctx.you ? 'You win' : `${name(ctx, w as Seat)} wins`}: ${v.result.reason ?? ''}`;
  }
  if (v.legal.length > 0) {
    if (mid) return 'Keep jumping: tap the next square.';
    return mustCapture ? 'Your move. You must capture.' : 'Your move.';
  }
  return `${name(ctx, v.turn as Seat)} ${v.turn === ctx.you ? 'are' : 'is'} to move.`;
}

export const checkersUi: GameUi = {
  id: 'checkers',
  privateHands: false,
  seatInfo: (ctx, s) => `${(ctx.msg.view as CheckersView).remaining[s] ?? 0} left`,
  render(ctx) {
    const v = ctx.msg.view as CheckersView;
    const me = ctx.mySeat;
    const over = v.result !== null;
    const sig = `${v.moves}-${over}`;
    if (ctx.local.get<string>('ck-sig') !== sig) {
      ctx.local.set('ck-sig', sig);
      ctx.local.set('ck-path', []);
      ctx.local.set('ck-resign', false);
    }
    const cur = ctx.local.get<number[]>('ck-path') ?? [];
    const setPath = (p: number[]) => ctx.local.set('ck-path', p);
    const legal = v.legal;
    const mine = legal.length > 0;
    const origins = new Set(legal.map((p) => p[0] as number));
    const cands = new Set(legal.filter((p) => cur.length > 0 && p.length > cur.length && startsWith(p, cur)).map((p) => p[cur.length] as number));
    const jumped = new Set(jumpedSquares(cur));
    const lastPath = new Set(v.last ?? []);
    const lastFrom = v.last?.[0];
    const lastTo = v.last?.[v.last.length - 1];
    const gone = new Set(v.lastCaptured);

    const tap = (i: number) => {
      if (cur.length <= 1 && origins.has(i)) return setPath(cur[0] === i ? [] : [i]);
      if (cur.length > 1 && i === cur[cur.length - 1]) return setPath(cur.slice(0, -1));
      if (!cands.has(i)) return;
      const next = [...cur, i];
      if (legal.some((p) => p.length === next.length && startsWith(p, next))) {
        setPath([]);
        ctx.act({ type: 'move', path: next });
      } else setPath(next);
    };

    const board = gridBoard({
      size: SIZE,
      flip: me === 1,
      testid: 'ck-board',
      coords: true,
      cell: ({ index: i, x, y }) => {
        if ((x + y) % 2 !== 0) return null;
        const p = v.board[i] as number;
        const owner = ownerOf(p);
        const tappable = mine && (origins.has(i) || cands.has(i) || (cur.length > 1 && i === cur[cur.length - 1]));
        const cls = [
          'ck-sq',
          cur.includes(i) ? 'sel' : '',
          cands.has(i) ? 'dest' : '',
          jumped.has(i) ? 'jumped' : '',
          mine && cur.length === 0 && origins.has(i) ? 'movable' : '',
          lastPath.has(i) ? 'trail' : '',
          i === lastFrom ? 'from' : '',
          i === lastTo ? 'to' : '',
          gone.has(i) ? 'gone' : '',
        ].filter(Boolean).join(' ');
        const tag = label(SIZE, i);
        return {
          classes: cls,
          label: owner === null ? tag : `${tag}, ${SEAT_NAMES[owner]} ${isKing(p) ? 'king' : 'man'}`,
          attrs: { 'data-testid': `ck-sq-${tag}`, 'data-sq': tag },
          ...(tappable ? { onTap: () => tap(i) } : {}),
          content:
            owner === null
              ? null
              : h(`span.ck-piece.s${owner}${isKing(p) ? '.king' : ''}`, { 'data-testid': `ck-piece-${tag}`, 'data-owner': owner, 'data-king': isKing(p) ? 1 : 0 }, isKing(p) ? '♛' : ''),
        };
      },
    });

    const mustCapture = mine && v.options.forcedCapture && legal.some(isCapture);
    const opp = me === null ? 1 : 1 - me;
    const self = me === null ? 0 : me;

    let controls: HTMLElement | null = null;
    if (me !== null && !over) {
      const armed = ctx.local.get<boolean>('ck-resign') === true;
      const offered = v.drawOffer !== null && v.drawOffer !== me;
      controls = h(
        'div.row.actions.ck-actions',
        { 'data-testid': 'ck-actions' },
        offered
          ? [
              h('button.primary', { 'data-testid': 'ck-accept', onclick: () => ctx.act({ type: 'acceptDraw' }) }, 'Accept draw'),
              h('button', { 'data-testid': 'ck-decline', onclick: () => ctx.act({ type: 'declineDraw' }) }, 'Decline'),
            ]
          : h('button', { 'data-testid': 'ck-offer', disabled: v.drawOffer === me, onclick: () => ctx.act({ type: 'offerDraw' }) }, v.drawOffer === me ? 'Draw offered' : 'Offer draw'),
        armed
          ? [
              h('button.danger', { 'data-testid': 'ck-resign-confirm', onclick: () => ctx.act({ type: 'resign' }) }, 'Confirm resign'),
              h('button', { 'data-testid': 'ck-resign-cancel', onclick: () => ctx.local.set('ck-resign', false) }, 'Cancel'),
            ]
          : h('button', { 'data-testid': 'ck-resign', onclick: () => ctx.local.set('ck-resign', true) }, 'Resign'),
      );
    }

    return h(
      'div.ck-table',
      {},
      player(ctx, v, opp, 'top'),
      board,
      player(ctx, v, self, 'bottom'),
      h('div.event', { 'data-testid': 'ck-status' }, statusText(ctx, v, cur.length > 1, mustCapture)),
      v.drawOffer !== null && !over ? h('div.muted', { 'data-testid': 'ck-offer-note' }, `${name(ctx, v.drawOffer)} offered a draw.`) : null,
      controls,
    );
  },
};
